// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupComponentProps } from "@insite/mobius/CheckboxGroup";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { mergeWithCss } from "@sc/XDL/Utilities";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    productsCount: number;
    isCondensed: boolean;
    hideCondensedSelector?: boolean;
    onChangeIsCondensed?: (event: React.SyntheticEvent, value: boolean) => void;
    extendedStyles?: CartLinesListHeaderStyles;
    // XNG-Change
    showEditCartIcon?: boolean;
}

// XNG-Change
const mapStateToProps = (state: ApplicationState) => {
    return {
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
    };
};

export interface CartLinesListHeaderStyles {
    wrapper?: InjectableCss;
    productCountText?: TypographyPresentationProps;
    checkboxGroup?: FieldSetGroupPresentationProps<CheckboxGroupComponentProps>;
    condensedCheckbox?: CheckboxPresentationProps;
    // XNG-Change
    titleWrapper?: InjectableCss;
    editCartLink?: LinkPresentationProps;
}

export const cartLinesListHeaderStyles: CartLinesListHeaderStyles = {
    wrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            border-bottom: 1px solid ${getColor("common.border")};
            padding-bottom: 10px;
        `,
    },
    productCountText: { weight: 600 },
    checkboxGroup: {
        css: css`
            @media print {
                display: none;
            }
        `,
    },
};
type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasHistory;
const CartLinesListHeader: FC<Props> = ({
    productsCount,
    isCondensed,
    hideCondensedSelector,
    onChangeIsCondensed,
    extendedStyles,
    // XNG-Change
    showEditCartIcon,
    history,
    cartPageUrl,
}) => {
    const [styles] = React.useState(() => mergeWithCss(cartLinesListHeaderStyles, extendedStyles));

    // XNG-Change
    const productsLabel = productsCount > 1 ? "Items" : "Item";

    // XNG-Change
    const navigateToCartPage = () => {
        if (cartPageUrl) {
            history.push(cartPageUrl);
        }
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.titleWrapper}>
                <Typography {...styles.productCountText} as="h3">
                    {productsCount} {translate(productsLabel)}
                </Typography>
                {/* XNG-Change */}
                {showEditCartIcon && (
                    <Link aria-label={translate("Edit Cart")} onClick={navigateToCartPage} {...styles.editCartLink}>
                        {translate("Edit Cart")}
                    </Link>
                )}
            </StyledWrapper>

            {!hideCondensedSelector && (
                <CheckboxGroup {...styles.checkboxGroup}>
                    <Checkbox {...styles.condensedCheckbox} checked={isCondensed} onChange={onChangeIsCondensed}>
                        {translate("Condensed View")}
                        <VisuallyHidden>{translate("for product list")}</VisuallyHidden>
                    </Checkbox>
                </CheckboxGroup>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(withHistory(CartLinesListHeader));
